"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle } from "lucide-react";
import { AeraButton, ModalCloseButton, ModalShell } from "@aera/ui";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  canUnmatchReceivable,
  fetchUnmatchImpact,
  formatUnmatchErrorMessage,
  isNoActiveAllocationImpact,
  postUnmatchPayment,
  type UnmatchCandidate,
} from "@/lib/receivableCorrections";
import { createOperationKey } from "@/lib/moneyOperations";
import { offerMoneyUndoToast, pickMoneyUndoSourceLabel } from "@/lib/moneyUndoToast";
import { pickReceivableId } from "@/app/artifacts/table/receivableHistory";
import { MoveAllocationModal } from "@/app/artifacts/table/ReceivableMoveResplitModals";

const RECEIVABLE_DRAWER_PANEL_SELECTOR =
  ".record-drawer-modal-panel[data-aera-modal-panel]";

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  return matches;
}

function UnmatchConfirmSheet({
  isOpen,
  onClose,
  busy = false,
  subtitle,
  detailLines = [],
  error,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  busy?: boolean;
  subtitle?: ReactNode;
  detailLines?: string[];
  error?: string | null;
  onConfirm: () => void;
}) {
  const isDesktop = useMinWidthMd();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!isDesktop) {
      setScopedHost(null);
      return;
    }
    if (!isOpen) return;
    setScopedHost(document.querySelector(RECEIVABLE_DRAWER_PANEL_SELECTOR));
  }, [isDesktop, isOpen]);

  const isScoped = isDesktop && scopedHost != null;

  const close = () => {
    if (busy) return;
    onClose();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={close}
      onExited={() => setScopedHost(null)}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={busy}
      zIndexClass="z-[10050]"
      panelClassName={[
        isScoped
          ? "flex flex-col overflow-hidden rounded-t-3xl rounded-b-[inherit] border-t border-aera-lightgray bg-aera-white shadow-xl"
          : "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div className="flex shrink-0 justify-center pb-1 pt-2">
        <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
      </div>

      <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-aera-charcoal">
            <span className="flex items-center gap-2 text-(--aera-dt-danger-text)">
              <AlertTriangle
                className="h-5 w-5 shrink-0"
                strokeWidth={2}
                aria-hidden
              />
              ยืนยันยกเลิกจับคู่
            </span>
          </h2>
          {subtitle ? (
            <p className="mt-1 text-sm leading-relaxed text-aera-gray">
              {subtitle}
            </p>
          ) : null}
        </div>
        <ModalCloseButton
          onClose={close}
          disabled={busy}
          className="-mr-1 shrink-0"
        />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-aera-charcoal">
        <p className="text-sm leading-relaxed text-aera-gray">
          การยกเลิกจับคู่จะคืนยอดกลับสลิปและเปิดยอดค้างกลับ โดยไม่สร้างรายรับใหม่
        </p>
        {detailLines.length > 0 ? (
          <ul className="mt-3 space-y-1.5 rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5">
            {detailLines.map((line) => (
              <li
                key={line}
                className="text-sm leading-relaxed text-aera-gray before:mr-1.5 before:text-aera-lightslate before:content-['•']"
              >
                {line}
              </li>
            ))}
          </ul>
        ) : null}
        {error ? (
          <p className="mt-3 rounded-xl bg-aera-red/10 px-3 py-2 text-xs font-medium text-aera-red">
            {error}
          </p>
        ) : null}
      </div>

      <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <AeraButton
          type="button"
          variant="danger"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          loading={busy}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? "กำลังยกเลิก…" : "ยืนยันยกเลิกจับคู่"}
        </AeraButton>
        <AeraButton
          type="button"
          variant="outline"
          fontWeight="medium"
          fullWidth
          className="cursor-pointer"
          disabled={busy}
          onClick={close}
        >
          ปิด
        </AeraButton>
      </div>
    </ModalShell>
  );
}

function candidateKey(candidate: UnmatchCandidate): string {
  return candidate.allocation_id || candidate.payment_id;
}

function formatCandidateAmount(candidate: UnmatchCandidate): string {
  const amount = Number.parseFloat(candidate.amount);
  if (!Number.isFinite(amount)) return candidate.amount?.trim() || "—";
  return `${amount.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} บาท`;
}

function formatCandidateTitle(candidate: UnmatchCandidate, index: number): string {
  const sender = candidate.sender_name?.trim();
  if (sender) return sender;
  return `สลิป ${index + 1}`;
}

function UnmatchCandidateSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="กำลังโหลดรายการจับคู่">
      <div className="space-y-2">
        <div className="h-3 w-20 animate-pulse rounded bg-aera-lightgray/80" />
        <ul className="space-y-2">
          {Array.from({ length: 2 }, (_, index) => (
            <li
              key={index}
              className="flex items-center justify-between gap-3 rounded-xl border border-aera-lightgray bg-white px-3.5 py-3"
            >
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="h-3.5 w-28 animate-pulse rounded bg-aera-lightgray/80" />
                <div className="h-2.5 w-24 animate-pulse rounded bg-aera-lightgray/70" />
              </div>
              <div className="h-4 w-4 shrink-0 animate-pulse rounded-full bg-aera-lightgray/80" />
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-2 rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5">
        <div className="h-2.5 w-full max-w-xs animate-pulse rounded bg-aera-lightgray/80" />
        <div className="h-2.5 w-full max-w-sm animate-pulse rounded bg-aera-lightgray/70" />
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="h-9 w-28 animate-pulse rounded-xl bg-aera-lightgray/80" />
        <div className="h-9 w-20 animate-pulse rounded-xl bg-aera-lightgray/70" />
      </div>
    </div>
  );
}

function UnmatchPreviewSkeleton() {
  return (
    <div
      className="space-y-2 rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5"
      aria-busy="true"
      aria-label="กำลังโหลดผลกระทบ"
    >
      <div className="h-2.5 w-full max-w-xs animate-pulse rounded bg-aera-lightgray/80" />
      <div className="h-2.5 w-full max-w-sm animate-pulse rounded bg-aera-lightgray/70" />
    </div>
  );
}

export function ReceivableCorrectionsPanel({
  item,
  ownerUserId,
  workspaceId,
  destinationCandidates = [],
  disabled = false,
  onApplied,
  onMatchCountChange,
}: {
  item: ReceivableListItem;
  ownerUserId: string | null;
  workspaceId: string | null;
  destinationCandidates?: ReceivableListItem[];
  disabled?: boolean;
  onApplied?: () => void | Promise<void>;
  /** Keep drawer tab badge in sync with the list the user actually sees. */
  onMatchCountChange?: (count: number) => void;
}) {
  const receivableId = pickReceivableId(item);
  const showUnmatch = canUnmatchReceivable(item);

  const [candidates, setCandidates] = useState<UnmatchCandidate[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [unmatchLines, setUnmatchLines] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [unmatchConfirmOpen, setUnmatchConfirmOpen] = useState(false);

  useEffect(() => {
    setCandidates([]);
    setSelectedKey(null);
    setUnmatchLines([]);
    setError(null);
    setLoading(true);
    setPreviewLoading(false);
    setMoveOpen(false);
    setUnmatchConfirmOpen(false);
  }, [item.mergeKey]);

  const canAct = Boolean(
    ownerUserId?.trim() && workspaceId?.trim() && receivableId && !disabled,
  );

  const selectedCandidate = candidates.find(
    (candidate) => candidateKey(candidate) === selectedKey,
  );

  const publishCandidates = useCallback(
    (next: UnmatchCandidate[]) => {
      setCandidates(next);
      onMatchCountChange?.(next.length);
    },
    [onMatchCountChange],
  );

  const loadUnmatchPreview = useCallback(async () => {
    if (!canAct || !receivableId || !ownerUserId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const impact = await fetchUnmatchImpact(receivableId, ownerUserId);

      // After the last unmatch (or already unmatched) — empty panel, not an error.
      if (isNoActiveAllocationImpact(impact)) {
        publishCandidates([]);
        setSelectedKey(null);
        setUnmatchLines([]);
        setError(null);
        return;
      }

      if (impact.ok === false && impact.candidates?.length) {
        publishCandidates(impact.candidates);
        const first = impact.candidates[0];
        const key = candidateKey(first);
        setSelectedKey(key);

        const retry = await fetchUnmatchImpact(
          receivableId,
          ownerUserId,
          first.payment_id,
        );
        if (isNoActiveAllocationImpact(retry)) {
          setUnmatchLines([]);
          return;
        }
        setUnmatchLines(retry.summary_lines ?? []);
        return;
      }

      if (impact.candidates?.length) {
        publishCandidates(impact.candidates);
        const preferred =
          impact.candidates.find((c) => c.payment_id === impact.payment_id) ??
          impact.candidates[0];
        setSelectedKey(candidateKey(preferred));
      } else if (impact.payment_id) {
        const single: UnmatchCandidate = {
          allocation_id: impact.allocation_id ?? "",
          payment_id: impact.payment_id,
          amount: impact.amount ?? "",
          sender_name: impact.sender_name?.trim() || undefined,
        };
        publishCandidates([single]);
        setSelectedKey(impact.allocation_id || impact.payment_id);
      } else {
        publishCandidates([]);
        setSelectedKey(null);
      }

      setUnmatchLines(impact.summary_lines ?? []);
    } catch (err) {
      publishCandidates([]);
      setSelectedKey(null);
      setUnmatchLines([]);
      setError(
        formatUnmatchErrorMessage(
          err instanceof Error ? err.message : "โหลดผลกระทบไม่สำเร็จ",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [canAct, ownerUserId, publishCandidates, receivableId]);

  const loadSelectedPreview = useCallback(async () => {
    if (!canAct || !receivableId || !ownerUserId || !selectedCandidate) return;
    setPreviewLoading(true);
    setError(null);

    try {
      const impact = await fetchUnmatchImpact(
        receivableId,
        ownerUserId,
        selectedCandidate.payment_id,
      );
      if (isNoActiveAllocationImpact(impact)) {
        setUnmatchLines([]);
        setError(null);
        return;
      }
      setUnmatchLines(impact.summary_lines ?? []);
    } catch (err) {
      setUnmatchLines([]);
      setError(
        formatUnmatchErrorMessage(
          err instanceof Error ? err.message : "โหลดผลกระทบไม่สำเร็จ",
        ),
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [canAct, ownerUserId, receivableId, selectedCandidate]);

  useEffect(() => {
    if (showUnmatch && canAct) void loadUnmatchPreview();
  }, [showUnmatch, canAct, loadUnmatchPreview]);

  useEffect(() => {
    if (!selectedCandidate || candidates.length <= 1) return;
    void loadSelectedPreview();
  }, [selectedKey, candidates.length, loadSelectedPreview, selectedCandidate]);

  if (!showUnmatch) return null;

  const handleUnmatch = async () => {
    if (!canAct || !receivableId || !ownerUserId || !workspaceId) return;
    if (!selectedCandidate) {
      setError("กรุณาเลือกรายการที่ต้องการยกเลิกจับคู่");
      setUnmatchConfirmOpen(false);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const result = await postUnmatchPayment({
        ownerUserId,
        workspaceId,
        receivableId,
        receivableItem: item,
        paymentId: selectedCandidate.payment_id,
        allocationId: selectedCandidate.allocation_id,
        operationKey: createOperationKey("unmatch"),
      });
      const opId =
        typeof result.after?.operation_id === "string"
          ? result.after.operation_id
          : null;
      offerMoneyUndoToast({
        message: "ยกเลิกจับคู่สำเร็จ",
        sourceLabel: [
          pickMoneyUndoSourceLabel(item),
          selectedCandidate.sender_name?.trim(),
        ]
          .filter(Boolean)
          .join(" → "),
        ownerUserId,
        workspaceId,
        operationId: opId,
        onUndone: async () => {
          await loadUnmatchPreview();
          await onApplied?.();
        },
      });
      setUnmatchConfirmOpen(false);
      // Stay on the tab — refresh list + badge so (N) updates immediately.
      const remaining = candidates.filter(
        (c) => candidateKey(c) !== candidateKey(selectedCandidate),
      );
      publishCandidates(remaining);
      if (remaining.length > 0) {
        setSelectedKey(candidateKey(remaining[0]));
        setUnmatchLines([]);
      } else {
        setSelectedKey(null);
        setUnmatchLines([]);
      }
      await loadUnmatchPreview();
      await onApplied?.();
    } catch (err) {
      setError(
        formatUnmatchErrorMessage(
          err instanceof Error ? err.message : "ยกเลิกจับคู่ไม่สำเร็จ",
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) p-4">
      <div>
        <h3 className="text-sm font-bold text-aera-charcoal">แก้ไขการจับคู่</h3>
        <p className="mt-1 text-xs text-aera-lightslate">
          ยกเลิกจับคู่ หรือย้ายยอดไปยอดค้างอื่นโดยไม่สร้างรายรับใหม่
        </p>
      </div>

      {loading ? (
        <UnmatchCandidateSkeleton />
      ) : (
        <>
          {candidates.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-aera-lightslate">
                เลือกรายการ
              </p>
              <ul className="space-y-2">
                {candidates.map((candidate, index) => {
                  const key = candidateKey(candidate);
                  const checked = selectedKey === key;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        disabled={busy || previewLoading}
                        onClick={() => setSelectedKey(key)}
                        className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors ${
                          checked
                            ? "border-aera-red bg-aera-redlight/40"
                            : "border-aera-lightgray bg-white hover:border-aera-red/40"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-aera-charcoal">
                            {formatCandidateTitle(candidate, index)}
                          </span>
                          <span className="mt-0.5 block text-xs tabular-nums text-aera-gray">
                            จับคู่ {formatCandidateAmount(candidate)}
                          </span>
                        </span>
                        <span
                          className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                            checked
                              ? "border-aera-red bg-aera-red"
                              : "border-aera-lightgray bg-white"
                          }`}
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5 text-xs text-aera-gray">
              ไม่มีการจับคู้อยู่แล้ว
            </p>
          )}

          {previewLoading ? (
            <UnmatchPreviewSkeleton />
          ) : unmatchLines.length > 0 ? (
            <ul className="space-y-1 rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5 text-xs text-aera-gray">
              {unmatchLines.map((line) => (
                <li key={line}>• {line}</li>
              ))}
            </ul>
          ) : null}

          {error ? (
            <p className="rounded-lg bg-aera-redlight px-3 py-2 text-sm font-medium text-aera-red">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <AeraButton
              type="button"
              variant="danger"
              size="sm"
              disabled={!canAct || busy || previewLoading || !selectedCandidate}
              onClick={() => {
                setError(null);
                setUnmatchConfirmOpen(true);
              }}
            >
              ยกเลิกจับคู่
            </AeraButton>
            <AeraButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={
                !canAct ||
                busy ||
                previewLoading ||
                !selectedCandidate?.allocation_id
              }
              onClick={() => setMoveOpen(true)}
            >
              ย้ายยอด
            </AeraButton>
          </div>
        </>
      )}

      <UnmatchConfirmSheet
        isOpen={unmatchConfirmOpen}
        onClose={() => setUnmatchConfirmOpen(false)}
        busy={busy}
        subtitle={
          selectedCandidate
            ? `${formatCandidateTitle(
                selectedCandidate,
                Math.max(
                  0,
                  candidates.findIndex(
                    (candidate) =>
                      candidateKey(candidate) ===
                      candidateKey(selectedCandidate),
                  ),
                ),
              )} · จับคู่ ${formatCandidateAmount(selectedCandidate)}`
            : undefined
        }
        detailLines={unmatchLines}
        error={error}
        onConfirm={() => void handleUnmatch()}
      />

      {selectedCandidate?.allocation_id ? (
        <MoveAllocationModal
          open={moveOpen}
          onOpenChange={setMoveOpen}
          sourceItem={item}
          allocationId={selectedCandidate.allocation_id}
          amount={selectedCandidate.amount}
          paymentId={selectedCandidate.payment_id}
          destinationCandidates={destinationCandidates}
          ownerUserId={ownerUserId}
          workspaceId={workspaceId}
          onMoved={async (operationId) => {
            setBusy(true);
            try {
              if (ownerUserId && workspaceId) {
                offerMoneyUndoToast({
                  message: "ย้ายยอดสำเร็จ",
                  sourceLabel: pickMoneyUndoSourceLabel(item),
                  ownerUserId,
                  workspaceId,
                  operationId,
                  onUndone: async () => {
                    await loadUnmatchPreview();
                    await onApplied?.();
                  },
                });
              }
              await loadUnmatchPreview();
              await onApplied?.();
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}
